import React from 'react';
import type { BillingAuditEntry } from '@v2/types';

interface BillingAuditTableProps {
  entries: BillingAuditEntry[];
  loading: boolean;
  error: string | null;
}

function changedCell(before: unknown, after: unknown, content: React.ReactNode): React.ReactNode {
  const changed = before !== after;
  return (
    <span className={changed ? 'tw-bg-yellow-100 tw-px-1 tw-rounded' : undefined}>
      {content ?? '-'}
    </span>
  );
}

function formatUtc(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
}

const BillingAuditTable: React.FC<BillingAuditTableProps> = ({ entries, loading, error }) => {
  if (loading) {
    return (
      <div className="tw-p-6 tw-text-center tw-text-gray-500 tw-bg-white tw-rounded tw-border tw-border-gray-200">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-p-4 tw-text-red-700 tw-bg-red-50 tw-rounded tw-border tw-border-red-200">
        Error loading audit log: {error}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="tw-p-6 tw-text-center tw-text-gray-400 tw-italic tw-bg-white tw-rounded tw-border tw-border-gray-200">
        No audit entries found for the selected filters.
      </div>
    );
  }

  return (
    <div className="tw-overflow-x-auto tw-bg-white tw-rounded tw-border tw-border-gray-200">
      <table className="tw-w-full tw-text-sm tw-border-collapse tw-text-gray-800">
        <thead>
          <tr className="tw-bg-gray-100 tw-text-left tw-text-gray-700">
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">When (UTC)</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Operator</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Practice</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Location</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Category Before</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Category After</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Billable Before</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Billable After</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Override Before</th>
            <th className="tw-px-3 tw-py-2 tw-font-semibold tw-border-b">Override After</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(e => (
            <tr key={e.id} className="tw-border-b hover:tw-bg-gray-50 tw-transition-colors">
              <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                {formatUtc(e.changedAtUtc)}
              </td>
              <td className="tw-px-3 tw-py-2">
                {e.operatorDisplayName ?? e.operatorAccountId ?? '-'}
              </td>
              <td className="tw-px-3 tw-py-2">{e.practiceName ?? '-'}</td>
              <td className="tw-px-3 tw-py-2">{e.locationName ?? '-'}</td>
              <td className="tw-px-3 tw-py-2">
                {changedCell(
                  e.beforeLocationCategory,
                  e.afterLocationCategory,
                  e.beforeLocationCategory,
                )}
              </td>
              <td className="tw-px-3 tw-py-2">
                {changedCell(
                  e.beforeLocationCategory,
                  e.afterLocationCategory,
                  e.afterLocationCategory,
                )}
              </td>
              <td className="tw-px-3 tw-py-2">
                {changedCell(
                  e.beforeIsBillable,
                  e.afterIsBillable,
                  String(e.beforeIsBillable ?? '-'),
                )}
              </td>
              <td className="tw-px-3 tw-py-2">
                {changedCell(
                  e.beforeIsBillable,
                  e.afterIsBillable,
                  String(e.afterIsBillable ?? '-'),
                )}
              </td>
              <td className="tw-px-3 tw-py-2">
                {changedCell(
                  e.beforeIsBillableOverridden,
                  e.afterIsBillableOverridden,
                  String(e.beforeIsBillableOverridden ?? '-'),
                )}
              </td>
              <td className="tw-px-3 tw-py-2">
                {changedCell(
                  e.beforeIsBillableOverridden,
                  e.afterIsBillableOverridden,
                  String(e.afterIsBillableOverridden ?? '-'),
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BillingAuditTable;
